import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { metricsApi } from "@/api/dealFlow/metrics";
import { useDealsList } from "@/hooks/deals/useDeals";
import { useB1Picker, useC1Picker, useUserDisplayMap } from "@/hooks/deals/useDealLookups";
import { STAGE_LABEL, SUB_STATES_BY_STAGE, SUB_STATE_LABEL } from "@/types/deals";
import type { DealPipelineStage } from "@/types/deals";
import { WidgetFrame } from "../../WidgetFrame";
import type { DashboardWidgetProps } from "../../types";
import { PanelBars } from "./shared";

/**
 * Manager / oversight panels absorbed from the retired
 * PipelineManagerDashboard page plus the Deal Flow metrics tiles. The
 * activeRole picks which pool the bars describe (C0 governs C1 intake,
 * E-tier governs B1 pre-approval) — a presentation choice only.
 */

export function WorkloadDistributionWidget({ widget, activeRole }: DashboardWidgetProps) {
  const role = activeRole === "C0" ? "C1" : "B1";
  const c1 = useC1Picker();
  const b1 = useB1Picker();
  const picker = role === "C1" ? c1 : b1;
  const display = useUserDisplayMap(picker.data ?? []);
  const dealsQuery = useDealsList({ pageSize: 500 });
  const allDeals = dealsQuery.data?.data ?? [];

  const counts = useMemo(() => {
    const m = new Map<string, number>();
    for (const d of allDeals) {
      const userId = role === "C1" ? d.assignedC1Id : d.assignedB1Id;
      if (!userId) continue;
      m.set(userId, (m.get(userId) ?? 0) + 1);
    }
    return Array.from(m.entries())
      .map(([userId, count]) => ({
        key: userId,
        label: display.get(userId) ?? userId,
        count,
      }))
      .sort((a, b) => b.count - a.count);
  }, [allDeals, display, role]);

  return (
    <WidgetFrame
      title={widget.title}
      subtitle={widget.subtitle ?? `${role} active deals`}
      to="/deal-flow/pipeline"
      isLoading={dealsQuery.isLoading || picker.isLoading}
      isError={dealsQuery.isError}
      isEmpty={counts.length === 0}
      emptyText={`No ${role}s have active deals to distribute.`}
    >
      <PanelBars rows={counts} />
    </WidgetFrame>
  );
}

export function StageFunnelWidget({ widget, activeRole }: DashboardWidgetProps) {
  const stage: DealPipelineStage = activeRole === "C0" ? "Intake" : "PreApproval";
  const dealsQuery = useDealsList({ pageSize: 500, stage: [stage] });
  const subStates = SUB_STATES_BY_STAGE[stage];

  const counts = useMemo(() => {
    const m = new Map<string, number>();
    for (const sub of subStates) m.set(sub, 0);
    for (const d of dealsQuery.data?.data ?? []) {
      m.set(d.subState, (m.get(d.subState) ?? 0) + 1);
    }
    return subStates.map((sub) => ({
      key: sub,
      label: SUB_STATE_LABEL[sub],
      count: m.get(sub) ?? 0,
    }));
  }, [dealsQuery.data, subStates]);

  return (
    <WidgetFrame
      title={widget.title}
      subtitle={widget.subtitle ?? STAGE_LABEL[stage]}
      to="/deal-flow/pipeline"
      isLoading={dealsQuery.isLoading}
      isError={dealsQuery.isError}
    >
      <PanelBars rows={counts} barClassName="bg-status-info" />
    </WidgetFrame>
  );
}

export function PipelineSnapshotWidget({ widget }: DashboardWidgetProps) {
  const metrics = useQuery({
    queryKey: ["deal-flow", "metrics", "pipeline", "dashboard"],
    queryFn: () => metricsApi.pipeline({}),
  });
  const buckets = metrics.data?.stageBuckets ?? [];

  return (
    <WidgetFrame
      title={widget.title}
      subtitle={widget.subtitle}
      to="/deal-flow/metrics"
      toLabel="Full metrics"
      isLoading={metrics.isLoading}
      isError={metrics.isError}
      isEmpty={buckets.length === 0}
      emptyText="No pipeline activity to chart yet."
    >
      <PanelBars
        rows={buckets.map((b) => ({
          key: b.stage,
          label: b.displayName,
          count: b.dealCount,
        }))}
      />
    </WidgetFrame>
  );
}

export function AnalystLoadWidget({ widget }: DashboardWidgetProps) {
  const metrics = useQuery({
    queryKey: ["deal-flow", "metrics", "analyst-load", "dashboard"],
    queryFn: () => metricsApi.analystLoad({}),
  });
  const buckets = metrics.data?.casesPerAnalyst ?? [];

  return (
    <WidgetFrame
      title={widget.title}
      subtitle={widget.subtitle}
      to="/deal-flow/metrics"
      toLabel="Full metrics"
      isLoading={metrics.isLoading}
      isError={metrics.isError}
      isEmpty={buckets.length === 0}
      emptyText="No active analyst cases right now."
    >
      <PanelBars
        rows={buckets.map((b) => ({
          key: b.analystUserId,
          label: `${b.displayName} (${b.roleCode})`,
          count: b.caseCount,
        }))}
      />
    </WidgetFrame>
  );
}
